import { useState } from "react";
import { Input } from "@shop-ledger/ui-components";

const ADD_NEW = "__add_new__";

/** Common retail units, in the order they're likely to be reached for — not
 * alphabetical, since "pcs"/"each" cover the large majority of non-bulk products and
 * shouldn't require scrolling past less-common weight/volume units to find. */
const COMMON_UNITS = ["pcs", "each", "kg", "g", "L", "mL", "box", "pack", "dozen", "case"];

export interface UnitOfMeasureSelectProps {
  id: string;
  label: string;
  value: string;
  onChange: (unit: string) => void;
  /** Units already in use on other items in this tenant's catalog (derived client-side
   * from the loaded inventory list, same source `CategorySelect`'s `existingCategories`
   * uses) — merged in ahead of "+ Add custom unit…" so a tenant's own established units
   * (e.g. a regional unit not in `COMMON_UNITS`) show up without forcing free text every
   * time. */
  existingUnits: string[];
}

/** Unit-of-measure field for the product form: a dropdown of common units (pcs, kg, L,
 * box, …) plus whatever units this tenant already uses elsewhere in its catalog, with a
 * "+ Add custom unit…" option that swaps in a free-text `Input` — same pattern
 * `CategorySelect` already established for category, replacing what used to always be a
 * bare text field here (business-owner request: pick from a list instead of retyping/
 * mistyping "kg" vs "Kg" vs "kgs" every time). */
export function UnitOfMeasureSelect({ id, label, value, onChange, existingUnits }: UnitOfMeasureSelectProps) {
  const [addingCustom, setAddingCustom] = useState(false);

  const presetUnits = Array.from(new Set([...COMMON_UNITS, ...existingUnits]));

  if (addingCustom) {
    return (
      <div className="sl-field">
        <Input id={id} label={label} value={value} onChange={(e) => onChange(e.target.value)} placeholder="e.g. carton, sack" />
        <button
          type="button"
          onClick={() => {
            setAddingCustom(false);
            onChange("");
          }}
          style={{
            alignSelf: "flex-start",
            background: "none",
            border: "none",
            padding: 0,
            font: "inherit",
            fontSize: "0.8125rem",
            color: "var(--sl-color-primary)",
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          Choose from common units instead
        </button>
      </div>
    );
  }

  return (
    <div className="sl-field">
      <label className="sl-field__label" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className="sl-input"
        value={presetUnits.includes(value) || value === "" ? value : ADD_NEW}
        onChange={(e) => {
          if (e.target.value === ADD_NEW) {
            setAddingCustom(true);
            onChange("");
          } else {
            onChange(e.target.value);
          }
        }}
      >
        <option value="">No unit specified</option>
        {presetUnits.map((unit) => (
          <option key={unit} value={unit}>
            {unit}
          </option>
        ))}
        <option value={ADD_NEW}>+ Add custom unit…</option>
      </select>
    </div>
  );
}
